import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = ['/images/Geobuilding.png', '/images/plane.png', '/images/plane3.png', '/images/plane4.png','/images/lidar.png', '/images/staff1.png'];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 100px)', // Subtract navbar height
        position: 'relative',
        overflow: 'hidden',
        mt: '100px', // Add margin to account for navbar
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          backgroundColor: '#000',
          opacity: isMobile ? 0.6 : 0.2,
          zIndex: 1,
        }}
      />

      {/* Background image carousel */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: { xs: '100%', md: '60%' },
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentImage}
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ 
              duration: 1.5,
              ease: "easeInOut",
              opacity: { duration: 1 },
              x: { duration: 1.5 }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <motion.img
              src={images[currentImage]}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              animate={{
                x: [0, 20, 0],
              }}
              transition={{
                duration: 5,
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "reverse"
              }}
              alt="GIS Mapping"
            />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Content */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          height: { xs: 'auto', md: '100vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2,
          py: { xs: 6, md: 0 },
          px: { xs: 2, md: 3 },
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Box sx={{ 
              maxWidth: '600px',
              mx: { xs: 'auto', md: 0 },
              textAlign: { xs: 'center', md: 'left' },
            }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography 
                  variant="h1" 
                  sx={{
                    color: isMobile ? 'white' : 'primary.main',
                    fontSize: { xs: '2.5rem', sm: '2.75rem', md: '3.5rem' },
                    fontWeight: 600,
                    mb: { xs: 4, md: 3 },
                    lineHeight: 1.2,
                    textShadow: isMobile ? '2px 2px 4px rgba(0,0,0,0.3)' : 'none',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Transforming the<br /> World Through Geospatial Tech
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography 
                  variant="h3"
                  sx={{ 
                    color: isMobile ? 'white' : '#000',
                    mb: { xs: 6, md: 4 },
                    lineHeight: 1.8,
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.3rem' },
                    textShadow: isMobile ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                    fontWeight: 400,
                    textAlign: { xs: 'center', md: 'left' },
                    px: { xs: 1, md: 0 },
                  }}
                >
                  Delivering innovative Geospatial technologies for<br/> sustainable development across the World.
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mt: { xs: -2, md: -1 },
                }}>
                  <Button
                    component={Link}
                    to="/solutions"
                    variant="contained"
                    size="large"
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '20px',
                      backgroundColor: isMobile ? 'white' : 'primary.main',
                      color: isMobile ? 'primary.main' : 'white',
                      py: 1,
                      px: 3,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.9)' : 'primary.dark',
                      },
                      maxWidth: { sm: '180px' },
                    }}
                  >
                    What we do
                  </Button>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: '20px',
                      borderColor: isMobile ? 'white' : 'primary.main',
                      color: isMobile ? 'white' : 'primary.main',
                      py: 1,
                      px: 3,
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: isMobile ? 'white' : 'primary.dark',
                        backgroundColor: isMobile ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      },
                      maxWidth: { sm: '180px' },
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </motion.div>
            </Box>
          </Grid>
        </Grid>
      </Container> 
    </Box>
  );
};

export default Hero; 